import { PHONE } from '@disco/constants/DeviceTypes'
import { updateItem } from '@disco/utils/immutable'
import {
  additionalMetadataKeys,
  getDropdownOptions,
} from '@disco/utils/metadata'
import { integerOnly } from '@disco/utils/string-utils'
import { Theme } from '@radix-ui/themes'
import cn from 'clsx'
import orderBy from 'lodash/orderBy'
import * as React from 'react'
import { MdAddCircleOutline } from '@react-icons/all-files/md/MdAddCircleOutline'
import Modal from 'react-modal'
import Button from 'components/ui/button'
import DatePicker from 'components/ui/date-picker'
import Dropdown from 'components/ui/dropdowns/native-dropdown'
import Heading from 'components/ui/heading'
import modalStyles from 'components/utils/modalStyles'
import FilterRow from '../filter-row'
const rangeOperators = [
  {
    value: 'equals',
    label: 'is',
  },
  {
    value: 'range',
    label: 'is between',
  },
]
const dateOperators = [
  ...rangeOperators,
  {
    value: 'before',
    label: 'is before',
  },
  {
    value: 'after',
    label: 'is after',
  },
]
const numberOperators = [
  ...rangeOperators,
  {
    value: 'less than',
    label: 'is less than',
  },
  {
    value: 'more than',
    label: 'is more than',
  },
]
const booleanOperators = [
  {
    value: 'true',
    label: 'true',
  },
  {
    value: 'false',
    label: 'false',
  },
]
const ranges = {
  year: dateOperators,
  number: numberOperators,
  date: dateOperators,
  partial_date: dateOperators,
}
const valueOperators = [
  '',
  'equals',
  'before',
  'after',
  'less than',
  'more than',
]
const emptyArray = []

const getBlankFilterValue = (key: string, keepValue: boolean = false) => {
  const result = {
    key,
    type: '',
    start: '',
    end: '',
  }

  // TODO: after API is stable remove this
  if (key === 'Release Date') {
    result.type = ranges.partial_date[0]?.value ?? ''
  }

  return keepValue ? result : { ...result, value: '' }
}

type MetadataKey = {
  type: string
  key: string
  text: string
  value?: string
  values?: string[]
}

const getKey = (metadataKeys: MetadataKey[], key: string) => {
  if (key.indexOf('disco_') === 0) {
    const backendParam = key.replace(/^disco_/, '')
    const additionalKey = additionalMetadataKeys.find(
      metadataKey => metadataKey.key === key,
    )
    if (!additionalKey) return null
    return {
      type: 'string',
      key: backendParam,
      value: additionalKey.value,
    } as MetadataKey
  }

  return metadataKeys.find(k => k.key === key)
}

const isRangeType = (metadataKeys: MetadataKey[], key: string) => {
  const metaKey = getKey(metadataKeys, key)
  return metaKey && Object.keys(ranges).includes(metaKey.type)
}

export default class Metadata extends React.Component<
  {
    updateSearchParams: (...args: Array<any>) => any
    addMetadataFilter: (...args: Array<any>) => any
    removeMetadataFilter: (...args: Array<any>) => any
    filters: Array<Record<string, any>>
    metadataKeys: MetadataKey[]
    mobileDeviceType?: string
    disabled?: boolean
  },
  {
    modalOpen: boolean
  }
> {
  _metaValue: HTMLInputElement | null | undefined
  state: {
    modalOpen: boolean
  } = {
    modalOpen: false,
  }
  _openModal: () => void = () =>
    this.setState({
      modalOpen: true,
    })
  _closeModal: () => void = () =>
    this.setState({
      modalOpen: false,
    })
  _handleAllClick: () => void = () => {
    this.props.updateSearchParams({
      filters: [
        getBlankFilterValue(
          this.props.metadataKeys.length > 0
            ? this.props.metadataKeys[0]?.key ?? ''
            : '',
        ),
      ],
    })
  }
  _handleFormSubmit: (e: React.SyntheticEvent<EventTarget>) => void = (
    e: React.SyntheticEvent<any>,
  ) => {
    e.preventDefault()
    if (!this._metaValue) return
    const value = this._metaValue.value

    // trigger actual search on submit on mobile
    this._handleMetadataChange(0, {
      value,
    })

    this._closeModal()
  }

  render(): React.ReactNode {
    const firstFilter = this.props.filters[0]
    const allMetadataClass = cn('SearchResults_FilterOption', {
      'is-active':
        !this.props.filters.length ||
        (this.props.filters.length === 1 &&
          !firstFilter?.value &&
          !firstFilter?.start &&
          !firstFilter?.end),
      SearchResults_FilterOption_Expandable_AlignTop:
        this.props.mobileDeviceType !== PHONE,
    })
    const metadataFilters =
      this.props.mobileDeviceType === PHONE
        ? this._renderMobileMetadataFilters()
        : this._renderMultipleMetadataFilters()
    return (
      <FilterRow caption="Filter by metadata:">
        <a onClick={this._handleAllClick} className={allMetadataClass}>
          Filter by
        </a>
        {metadataFilters}
      </FilterRow>
    )
  }

  _handleMetadataChange: (keyIndex: number, metadataDiff: any) => void = (
    keyIndex: number,
    metadataDiff: Record<string, any>,
  ) => {
    const updatedFilters = updateItem(this.props.filters, keyIndex, {
      ...this.props.filters[keyIndex],
      ...metadataDiff,
    })
    this.props.updateSearchParams({
      filters: updatedFilters,
    })
  }
  _handleRangeChange: (
    keyIndex: number,
    prop: string,
    propValue: string,
  ) => void = (keyIndex: number, prop: string, propValue: string) => {
    this._handleMetadataChange(keyIndex, {
      [prop]: integerOnly(propValue),
    })
  }
  _onAddFilter: (key: string) => any = (key: string) =>
    this.props.addMetadataFilter(getBlankFilterValue(key))
  _onRemoveFilter: (index: number) => any = (index: number) =>
    this.props.removeMetadataFilter(index)
  _handleChangeMetadataKey: (
    keyIndex: number,
    currentKey: string,
    targetKey: string,
  ) => void = (keyIndex: number, currentKey: string, targetKey: string) => {
    const { metadataKeys } = this.props
    const keepValue =
      !isRangeType(metadataKeys, currentKey) &&
      !isRangeType(metadataKeys, targetKey)

    this._handleMetadataChange(
      keyIndex,
      getBlankFilterValue(targetKey, keepValue),
    )
  }

  _renderMultipleMetadataFilters(): React.ReactElement<'div'> {
    const { filters, metadataKeys } = this.props
    const filterList = filters.map((filter, keyIndex) => {
      const { key, value, type, start, end } = filter
      const exclusionList = this.props.filters
        .filter(filterValue => filterValue.key !== key)
        .map(f => f.key)
      let options = getDropdownOptions(
        this.props.metadataKeys,
        additionalMetadataKeys,
        exclusionList,
      )
      const newFilterKey = options.filter(o => o.value !== key)[0]?.value || ''
      const actionButton =
        !this.props.disabled &&
        filters.length < 5 &&
        keyIndex === filters.length - 1 &&
        (filter.value !== '' || start || end) ? (
          <a
            className="SearchAction_Button"
            onClick={() => this._onAddFilter(newFilterKey.toString())}
            title="Add another filter"
          >
            <MdAddCircleOutline />
          </a>
        ) : null
      const removeButton =
        filter.value || filter.start || filter.end || filters.length > 1 ? (
          <a
            className="SearchAction_Button SearchAction_FilterRemoveButton"
            onClick={() => this._onRemoveFilter(keyIndex)}
          >
            <MdAddCircleOutline />
          </a>
        ) : null
      const isRange = isRangeType(metadataKeys, key)
      // order options in dropdown alphabetically
      options = orderBy(
        options,
        [option => option.label.toLowerCase()],
        ['asc'],
      )
      const metadataFiltersDropdown = (
        <div className="col-xs-12 col-sm-3 SearchDropdown">
          <Dropdown
            className="SelectDropdown"
            options={options}
            value={key}
            onChange={e =>
              this._handleChangeMetadataKey(keyIndex, key, e.target.value)
            }
            disabled={this.props.disabled}
          />
        </div>
      )
      const actionButtons = (
        <div className="col-xs-6 col-sm-2 flc">
          {removeButton} {actionButton}
        </div>
      )
      const metaKey = getKey(metadataKeys, key)
      if (!metaKey) return null
      const valueInput = this.getValueInput(
        metaKey.type,
        value,
        isRange,
        keyIndex,
      )

      if (
        metaKey.type === 'boolean' ||
        metaKey.type === 'dropdown_single' ||
        metaKey.type === 'dropdown_multi'
      ) {
        const dropdownOptions = metaKey.values
          ? metaKey.values.map(v => ({
              value: v,
              label: v,
            }))
          : emptyArray
        return (
          <div className="row" key={keyIndex}>
            {metadataFiltersDropdown}
            <div className="col-xs-12 col-sm-3 SearchDropdown">
              <Dropdown
                className="SelectDropdown"
                options={
                  metaKey.type === 'boolean'
                    ? booleanOperators
                    : dropdownOptions
                }
                value={value}
                caption="Choose value..."
                onChange={e => {
                  this._handleMetadataChange(keyIndex, {
                    // for boolean fields backend doesn't expect array
                    value:
                      metaKey.type === 'boolean'
                        ? e.target.value
                        : [e.target.value],
                  })
                }}
                disabled={this.props.disabled}
              />
            </div>
            {actionButtons}
          </div>
        )
      }

      // regular filter
      if (!isRange) {
        return (
          <div className="row" key={keyIndex}>
            {metadataFiltersDropdown}
            <div className="col-xs-12 col-sm-7">{valueInput}</div>
            {actionButtons}
          </div>
        )
      }

      // return range filter
      if (
        metaKey.type === 'year' ||
        metaKey.type === 'number' ||
        metaKey.type === 'partial_date' ||
        metaKey.type === 'date'
      )
        return (
          <div className="row" key={keyIndex}>
            {metadataFiltersDropdown}
            <div className="col-xs-12 col-sm-2 SearchDropdown">
              <Dropdown
                className="SelectDropdown"
                options={ranges[metaKey.type]}
                value={type}
                onChange={e =>
                  this._handleMetadataChange(keyIndex, {
                    type: e.target.value,
                  })
                }
                disabled={this.props.disabled}
              />
            </div>
            {(!type || valueOperators.indexOf(type) > -1) && (
              <div className="col-xs-12 col-sm-4">{valueInput}</div>
            )}
            {type === 'range' && (
              <div className="col-xs-12 col-sm-5 flc">
                {this.getRangeInput(metaKey.type, start, keyIndex, 'start')}
                <span className="SearchRangeSeparator">&#8211;</span>
                {this.getRangeInput(metaKey.type, end, keyIndex, 'end')}
              </div>
            )}
            {actionButtons}
          </div>
        )
    })
    return (
      <div
        className="SearchResults_FilterOptionsContainer"
        title={
          this.props.disabled
            ? "To search by metadata, please choose 'All' or 'Tracks' above the search results."
            : undefined
        }
      >
        {filterList}
      </div>
    )
  }

  getRangeInput(
    metadataType: string,
    value: string,
    keyIndex: number,
    key: string,
  ): React.ReactElement<'div'> | React.ReactElement<'input'> {
    if (metadataType === 'partial_date' || metadataType === 'date') {
      return (
        <div className="calendar is-half SearchResults_FilterOption SearchResults_FilterDate">
          <DatePicker
            compact={false}
            value={value}
            disabled={this.props.disabled}
            onChange={date =>
              this._handleMetadataChange(keyIndex, {
                [key]: date,
              })
            }
          />
        </div>
      )
    }

    return (
      <input
        className="SearchInput is-half"
        value={value}
        onChange={e => this._handleRangeChange(keyIndex, key, e.target.value)}
        disabled={this.props.disabled}
      />
    )
  }

  getValueInput(
    metadataType: string,
    value: string,
    isRange: boolean | null | undefined,
    keyIndex: number,
  ): React.ReactNode {
    return (
      <React.Fragment>
        {metadataType === 'partial_date' || metadataType === 'date' ? (
          <div className="calendar is-full SearchResults_FilterOption SearchResults_FilterDate">
            <DatePicker
              compact={false}
              value={value}
              disabled={this.props.disabled}
              onChange={date =>
                this._handleMetadataChange(keyIndex, {
                  value: date,
                })
              }
            />
          </div>
        ) : (
          <input
            className="SearchInput"
            value={value}
            onChange={e => {
              if (!isRange) {
                this._handleMetadataChange(keyIndex, {
                  value: e.target.value,
                })
              } else {
                this._handleRangeChange(keyIndex, 'value', e.target.value)
              }
            }}
            disabled={this.props.disabled}
          />
        )}
      </React.Fragment>
    )
  }

  _renderMobileMetadataFilters(): null | React.ReactElement<'div'> {
    if (this.props.disabled) return null
    const hasFilters = this.props.filters.length && this.props.filters[0]?.value
    let withMeta = 'Metadata Field'

    if (hasFilters) {
      withMeta = this.props.filters.map(filter => {
        const value = filter.value

        if (filter.key.indexOf('disco_') === 0) {
          const f = additionalMetadataKeys.find(
            metadataKey => metadataKey.key === filter.key,
          )
          if (!f) return value
          const filterTitle = f.filterTitle
          return `${filterTitle}: ${value}`
        }

        const key = filter.key.split('_')
        return `${key[key.length - 1]}: ${value}`
      })[0]
    }

    const isActive = hasFilters ? ' is-active' : ''
    return (
      <div>
        <a
          className={`SearchResults_FilterOption${isActive}`}
          onClick={this._openModal}
        >
          {withMeta}
        </a>
        {this._renderModal()}
      </div>
    )
  }

  _renderModal(): null | React.ReactNode {
    if (!this.state.modalOpen) return null
    const options = getDropdownOptions(
      this.props.metadataKeys,
      additionalMetadataKeys,
      [],
    )
    const filter = this.props.filters[0]
    return (
      <Modal
        onRequestClose={this._closeModal}
        isOpen={this.state.modalOpen}
        style={modalStyles}
      >
        <Theme>
          <div className="Form" onSubmit={this._handleFormSubmit}>
            <Heading>Search by Metadata Field</Heading>
            <div className="row">
              <div className="col-xs-12 col-sm-6">
                <label className="Form_Label">Metadata Key</label>
                <Dropdown
                  className="SelectDropdown"
                  options={options}
                  value={filter?.key ?? ''}
                  onChange={e =>
                    this._handleMetadataChange(
                      0,
                      getBlankFilterValue(e.target.value),
                    )
                  }
                />
              </div>
              <div className="col-xs-12 col-sm-6">
                <label className="Form_Label">Metadata Value</label>
                <input
                  ref={r => {
                    this._metaValue = r
                  }}
                  className="Form_Input"
                />
              </div>
            </div>
            <div className="row center-xs start-sm">
              <div className="col-xs-12 col-sm-5">
                <Button
                  appearance="primary"
                  caption="Update Search Filters"
                  onClick={this._handleFormSubmit}
                />
              </div>
              <div className="col-xs-12 col-sm-3">
                <Button caption="Cancel" onClick={this._closeModal} />
              </div>
            </div>
          </div>
        </Theme>
      </Modal>
    )
  }
}
